import { Box } from "@chakra-ui/layout";
import { FC, useRef, MouseEvent, useEffect, useState } from "react";
import { useDrag, useDrop } from "react-dnd";
import { DndDropZone } from "./dndDropZone";
import { DndGroupRow } from "./dndGroupRow";
import { SingleAsset } from "./singleAsset";
import { ItemLevels } from "lib/hooks/usePackAssets";
import { countAssetsInGroup } from "./helpers";
import { getEmptyImage } from "react-dnd-html5-backend";
import { GroupDragHandle } from "./drag-handles";
import { useDragScrolling, useLoggedinUser, usePack } from "lib/hooks";
import { useDnd } from "./context";
import { usePackContext } from "domains/pack";
import { isAdmin, isOwner, isPreviewingPack } from "lib/utils";
import { useRouter } from "next/router";

type Props = {
  data: any;
  handleDrop: Function;
  path: string;
  onSelectItem: (e: MouseEvent<HTMLElement>, path: string) => void;
  selectedAssets: any[];
  layout: any;
}

export const DndGroup: FC<Props> = ({
  data, handleDrop, path, onSelectItem, selectedAssets, layout
}) => {
  const ref = useRef(null);
  const [ isHovered, setIsHovered ] = useState<boolean>(false);
  const { addEventListenerForWindow, removeEventListenerForWindow } = useDragScrolling();
  const { setIsDragInProgress } = useDnd();
  const { packId } = usePackContext();
  const { data: pack } = usePack(packId);
  const { data: user } = useLoggedinUser();
  const { pathname } = useRouter();

  const [{ isOver }, drop] = useDrop({
    accept: [ ItemLevels.ASSET, ItemLevels.GROUP, ItemLevels.ROW ],
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    })
  })

  const [{ isDragging }, drag, preview] = useDrag({
    type: ItemLevels.GROUP,
    item: () => {
      addEventListenerForWindow();
      return {
        id: data.id,
        children: data.children,
        totalAssets: countAssetsInGroup(data),
        level: ItemLevels.GROUP,
        path,
        selectedAssets,
      }
    },
    canDrag: () => (isAdmin(pack) || isOwner(pack, user)) && !isPreviewingPack(pathname),
    end: () => {
      removeEventListenerForWindow();
    },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging()
    })
  }, [ data, path, selectedAssets ]);

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true })
  }, [preview]);

  useEffect(() => {
    setIsDragInProgress(isDragging)
  }, [isDragging, setIsDragInProgress]);

  const opacity = isDragging ? 0.3 : 1;
  const isSelected = selectedAssets.includes(path);

  const renderRow = (currentPath: string, asset) => (
    <Box key={`${asset.id}-row`}>
      <DndDropZone
        layout={layout}
        data={{
          path: currentPath,
          groupId: data.id,
          rowId: asset.id,
          childrenCount: data.children.length,
        }}
        onDrop={handleDrop}
      />
      <DndGroupRow
        layout={layout}
        data={asset}
        handleDrop={handleDrop}
        path={currentPath}
        selectedAssets={selectedAssets}
      />
    </Box>
  )

  drag(ref);

  return (
    <Box
      ref={drop}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      opacity={opacity}
      position="relative"
      layerStyle={['with-transition']}
      mt="0"
      mb="0"
      _before={{
        opacity: (isSelected || isOver) ? 1 : 0,
        content: "' '",
        position: 'absolute',
        zIndex: -100,
        top: 0,
        right: '-12px',
        bottom: 0,
        left: '-12px',
        border: '1px dashed',
        borderColor: 'brand.500',
        borderRadius: '14px',
      }}
      _hover={{
        _before: {
          opacity: 1,
        }
      }}
    >
      <GroupDragHandle
        ref={ref}
        isGroupActive={isOver || isSelected || isHovered}
        onSelectGroup={(e) => onSelectItem(e, path)}
      />
      {data.children.map((asset, index: number) => {
        const currentPath: string = `${path}-${index}`;

        if (asset.level === ItemLevels.ROW) {
          return renderRow(currentPath, asset)
        }

        return (
          <Box key={asset.id}>
            <DndDropZone
              layout={layout}
              data={{
                path: currentPath,
                groupId: data.id,
                childrenCount: data.children.length,
              }}
              onDrop={handleDrop}
            />
            <SingleAsset
              data={asset}
              selectedAssets={selectedAssets}
              path={currentPath}
            />
          </Box>
        );
      })}
      <DndDropZone
        layout={layout}
        data={{
          path: `${path}-${data.children.length}`,
          childrenCount: data.children.length,
          groupId: data.id,
        }}
        onDrop={handleDrop}
      />
    </Box>
  )
};
